import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { migrationData, species } from '../../mocks/migration';
import { useCart } from '../../hooks/useCart';

export default function MigrationPage() {
  const [selectedState, setSelectedState] = useState('Texas');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const stateData = migrationData[selectedState as keyof typeof migrationData];
  const { totalCount } = useCart();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/migration#webpage`,
          url: `${siteUrl}/migration`,
          name: 'Hummingbird Migration Guide – Track Arrival Dates by State | Front Porch Nectar',
          description: 'Interactive hummingbird migration guide. Find out when hummingbirds arrive in your state, track spring arrival and fall departure dates, and learn feeding tips.',
          isPartOf: { '@id': `${siteUrl}/#website` },
        },
        {
          '@type': 'Article',
          headline: 'Hummingbird Migration Guide – When Do Hummingbirds Arrive in Your State?',
          description: 'Track hummingbird migration patterns across the US. Find spring arrival, peak season, and fall departure dates for every state.',
          image: 'https://public.readdy.ai/ai/img_res/b7172837b96e58e291aee7766722ca95.jpg',
          author: { '@type': 'Organization', name: 'Front Porch Nectar' },
          publisher: {
            '@type': 'Organization',
            name: 'Front Porch Nectar',
            logo: {
              '@type': 'ImageObject',
              url: 'https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png',
            },
          },
          url: `${siteUrl}/migration`,
          mainEntityOfPage: `${siteUrl}/migration`,
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-migration';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-migration');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png" 
              alt="Front Porch Nectar Logo" 
              className="h-10 sm:h-14 w-auto max-w-[160px] sm:max-w-none object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[#2C3E50] font-medium hover:text-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/product/16oz" className="text-[#2C3E50] font-medium hover:text-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer">Products</Link>
            <Link to="/migration" className="text-[#FF6B6B] font-medium whitespace-nowrap cursor-pointer">Migration</Link>
            <Link to="/checkout" className="relative cursor-pointer">
              <i className="ri-shopping-cart-line text-2xl text-[#2C3E50] hover:text-[#FF6B6B] transition-colors"></i>
              {totalCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center">
                  {totalCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Nav Icons */}
          <div className="flex md:hidden items-center gap-4">
            <Link to="/checkout" className="relative cursor-pointer">
              <i className="ri-shopping-cart-line text-2xl text-[#2C3E50]"></i>
              {totalCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FF6B6B] text-white text-xs rounded-full flex items-center justify-center">
                  {totalCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="text-[#2C3E50] cursor-pointer w-8 h-8 flex items-center justify-center"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-3 mx-0 bg-white border-t border-gray-100">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Home</Link>
            <Link to="/product/16oz" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#2C3E50] font-medium border-b border-gray-100 cursor-pointer">Products</Link>
            <Link to="/migration" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-4 text-[#FF6B6B] font-medium cursor-pointer">Migration</Link>
          </div>
        )}
      </nav>

      {/* Hero Banner */}
      <section className="relative h-64 sm:h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20hummingbird%20in%20mid%20flight%20with%20wings%20spread%20against%20blurred%20natural%20background%20vibrant%20iridescent%20colors%20professional%20wildlife%20photography%20dynamic%20motion%20beautiful%20lighting&width=1920&height=600&seq=migrationhero&orientation=landscape"
            alt="Hummingbird in flight"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 sm:px-6">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-3 sm:mb-4 tracking-wide leading-tight">
            HUMMINGBIRD MIGRATION GUIDE
          </h1>
          <p className="text-base sm:text-xl text-white font-light mb-6 sm:mb-8">
            Track when these amazing birds visit your region
          </p>
          <Link
            to="/product/16oz"
            className="inline-flex items-center gap-3 px-8 sm:px-10 py-3 sm:py-4 bg-[#FF6B6B] hover:bg-[#e85d5d] text-white rounded-full font-bold text-base sm:text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-bag-line text-xl"></i>
            SHOP NOW
          </Link>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-bold text-center text-[#2C3E50] mb-8 sm:mb-12">
            WHEN WILL HUMMINGBIRDS ARRIVE?
          </h2>
          
          <div className="max-w-2xl mx-auto mb-8 sm:mb-12">
            <label className="block text-sm font-bold text-[#2C3E50] mb-3">SELECT YOUR STATE</label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 border-2 border-gray-300 rounded-full focus:border-[#FF6B6B] focus:outline-none transition-colors text-base sm:text-lg font-semibold cursor-pointer"
            >
              {Object.keys(migrationData).map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-3xl p-4 sm:p-8 shadow-lg mb-8 sm:mb-12">
            <img 
              src="https://readdy.ai/api/search-image?query=illustrated%20map%20of%20united%20states%20showing%20hummingbird%20migration%20routes%20with%20color%20coded%20regions%20arrows%20indicating%20flight%20paths%20clean%20educational%20infographic%20style%20professional%20design&width=1200&height=600&seq=usamap&orientation=landscape"
              alt="US Migration Map"
              className="w-full h-auto rounded-2xl mb-6 sm:mb-8"
            />
            
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-400 rounded flex-shrink-0"></div>
                <span className="text-gray-600">Early Arrival (Feb-Mar)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-yellow-400 rounded flex-shrink-0"></div>
                <span className="text-gray-600">Mid Season (Apr-May)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-400 rounded flex-shrink-0"></div>
                <span className="text-gray-600">Late Arrival (May-Jun)</span>
              </div>
            </div>
          </div>

          {/* Migration Timeline */}
          <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-lg">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#2C3E50] mb-6 sm:mb-8 text-center">
              {selectedState} Migration Timeline
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-[#2C3E50] mb-2">SPRING ARRIVAL</h4>
                <p className="text-xl sm:text-2xl font-bold text-[#FF6B6B]">{stateData.spring}</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FFD93D] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-sun-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-[#2C3E50] mb-2">PEAK SEASON</h4>
                <p className="text-xl sm:text-2xl font-bold text-[#FF6B6B]">{stateData.peak}</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-leaf-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-[#2C3E50] mb-2">FALL DEPARTURE</h4>
                <p className="text-xl sm:text-2xl font-bold text-[#FF6B6B]">{stateData.fall}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Species Information */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-bold text-center text-[#2C3E50] mb-8 sm:mb-12">
            COMMON SPECIES IN YOUR AREA
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {species.map((bird) => (
              <div key={bird.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 group cursor-pointer">
                <div className="h-48 sm:h-64 overflow-hidden">
                  <img 
                    src={bird.image}
                    alt={bird.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-[#2C3E50] mb-2">{bird.name}</h3>
                  <p className="text-sm text-gray-500 italic mb-3">{bird.scientificName}</p>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm sm:text-base">{bird.description}</p>
                  <div className="flex items-center gap-2 text-sm text-[#FF6B6B] font-semibold">
                    <i className="ri-map-pin-line flex-shrink-0"></i>
                    <span>{bird.range}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feeding Tips */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-[#E8F4F8]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#2C3E50] mb-6 sm:mb-8">
                FEEDING TIPS DURING MIGRATION
              </h2>
              
              <div className="space-y-5 sm:space-y-6">
                {[
                  { num: '1', title: 'Prepare feeders early', desc: 'Set up feeders 1-2 weeks before expected arrival dates to catch early migrants' },
                  { num: '2', title: 'Keep nectar fresh', desc: 'Change nectar every 3-5 days, more frequently in hot weather to prevent fermentation' },
                  { num: '3', title: 'Clean feeders regularly', desc: 'Wash feeders with hot water and scrub thoroughly to prevent mold and bacteria' },
                  { num: '4', title: 'Provide multiple feeding stations', desc: 'Space feeders apart to reduce territorial disputes and accommodate more birds' }
                ].map((tip) => (
                  <div key={tip.num} className="flex gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-lg sm:text-2xl font-bold text-white">{tip.num}</span>
                    </div>
                    <div>
                      <h4 className="text-base sm:text-xl font-bold text-[#2C3E50] mb-1 sm:mb-2">{tip.title}</h4>
                      <p className="text-gray-600 text-sm sm:text-base">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/edited_079feef23673fcb7a8d7bee36f18c854_59c08b4a.jpg"
                alt="Proper feeder setup"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            STOCK UP FOR MIGRATION SEASON
          </h2>
          <p className="text-white text-base sm:text-xl mb-6 sm:mb-8">
            Get 15% off when you buy 3 or more pouches
          </p>
          <Link 
            to="/product/16oz"
            className="inline-flex items-center gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-white text-[#2C3E50] rounded-full font-bold text-base sm:text-lg hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
          >
            SHOP NOW
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FF6B6B] text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/b04a437c-0005-402a-b32c-9c2676d2dc8b.png" 
                alt="Front Porch Nectar" 
                className="h-14 sm:h-20 w-auto max-w-[180px] object-contain mb-5 sm:mb-6"
              />
              <p className="text-white/90 leading-relaxed mb-5 sm:mb-6 text-sm sm:text-base">
                Premium hummingbird nectar made with 100% natural ingredients. Trusted by bird lovers across North America for over a decade.
              </p>
              <div className="flex gap-3 sm:gap-4">
                <a href="https://www.facebook.com/profile.php?id=61586247152152" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-facebook-fill"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-instagram-line"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-twitter-x-line"></i></a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-[#FF6B6B] transition-all cursor-pointer"><i className="ri-youtube-fill"></i></a>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-bold mb-4 tracking-wider">CONTACT</h3>
              <p className="mb-4 text-sm sm:text-base break-all">support@frontporchnectar.com</p>
              <h3 className="text-sm font-bold mb-4 tracking-wider">CUSTOMER SERVICE</h3>
              <p className="mb-4 text-sm sm:text-base">1-800-NECTAR-1</p>
              <h3 className="text-sm font-bold mb-4 tracking-wider">HOURS</h3>
              <p className="text-sm sm:text-base">Mon-Fri: 9AM - 6PM EST</p>
            </div>
            
            <div className="sm:col-span-2 lg:col-span-1">
              <img 
                src="https://readdy.ai/api/search-image?query=beautiful%20hummingbird%20at%20red%20feeder%20close%20up%20natural%20lighting%20vibrant%20colors%20professional%20wildlife%20photography%20vertical%20composition%20stunning%20detail&width=300&height=400&seq=footerbird3&orientation=portrait"
                alt="Hummingbird at feeder"
                className="w-full h-56 sm:h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/80 text-sm text-center sm:text-left">© 2025 Front Porch Nectar. All rights reserved</p>
            <div className="flex gap-4 sm:gap-6 text-sm flex-wrap justify-center">
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Terms</a>
              <span className="text-white/40">/</span>
              <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">Shipping</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
